import React from 'react';
import BaseFactory from './BaseFactory';
import {
  hasBrandName,
  getBrandName,
  getFaqsAnchorTag,
  getBlogAnchorTag,
  hasLocationHours,
  getClinicAnchorTag,
  getCityAnchorTag,
  getNearbyBrandLocation,
  getLocationDistanceInMiles,
  hasAtLeast2NearbyLocations,
  NoTopMarginParagraph,
  NoBottomMarginParagraph,
  hasCustomDescription,
} from './util';
import { isEmptyObject } from '../../../core/util/object';
import CustomDescriptionSection from './CustomDescriptionSection';
import { SOLV_SECTION_TITLE } from './constants';

class NonPartnerUrgentCareV3 extends BaseFactory {
  constructor(location, nearbyLocations, properties) {
    super(location, properties);
    this.nearbyLocations = nearbyLocations;

    // nearby locations
    if (!isEmptyObject(nearbyLocations)) {
      // nearby brand location
      const nearbyBrandClinic = getNearbyBrandLocation(nearbyLocations);
      if (!isEmptyObject(nearbyBrandClinic)) {
        this.brandName = getBrandName(nearbyBrandClinic);
        this.nearbyBrandClinicAnchorTag = getClinicAnchorTag(nearbyBrandClinic, 'sister facility');
        this.nearbyBrandClinicCityAnchorTag = getCityAnchorTag(nearbyBrandClinic);
        this.nearbyBrandClinicDistance = getLocationDistanceInMiles(nearbyBrandClinic);
      }
    }
  }

  getDescription() {
    return (
      `${this.clinicName} is a ${this.clinicalCategory} located ${this.address}, ${this.city}, ` +
      `${this.state}, ${this.zip} providing immediate, non-life-threatening healthcare` +
      `services to the ${this.city} area. For more information, call ${this.clinicName}` +
      `${this.phoneString}.`
    );
  }

  getKeywords() {
    return (
      `${this.clinicName}, ${this.clinicName}, ${this.city}, ${this.clinicName} ${this.zip}, ` +
      `urgent care ${this.city}, urgent care ${this.zip}, ` +
      `walk-in clinic ${this.city}, walk-in clinic ${this.zip}, ${this.city} ${this.state} ` +
      `clinic, ${this.zip} clinic`
    );
  }

  getBody() {
    return (
      <span>
        <CustomDescriptionSection location={this.location} />
        <NoTopMarginParagraph>
          {hasCustomDescription(this.location) && (
            <span>
              {SOLV_SECTION_TITLE}
              <br />
            </span>
          )}
          With only a few walk-in clinics nearby, {this.clinicName} may be your best option for
          immediate care in {this.cityAnchorTag}.{' '}
          {hasLocationHours(this.location) && (
            <span>
              They are open {this.nextOpenDayName} from {this.nextOpenDayHoursStr}, as well as{' '}
              {this.numDaysOpenString} a week.{' '}
            </span>
          )}
          As long as you visit them during their business hours, you should be able to walk right
          into {this.fullAddress} and be seen relatively quickly. The national average for patients
          waiting at an urgent care is between 15-30 minutes, so hopefully it shouldn't be too long
          of a wait.
        </NoTopMarginParagraph>
        {hasAtLeast2NearbyLocations(this.nearbyLocations) && (
          <p>
            If you need a 24-hour option, you can always visit the closest emergency room, of
            course, you will likely pay 3-5 times more and have a much longer wait. In the case of a
            medical emergency, call 911.
          </p>
        )}
        {hasBrandName(this.nearbyLocations) && (
          <p>
            As part of the {this.brandName} group, they have a {this.nearbyBrandClinicAnchorTag} in{' '}
            {this.nearbyBrandClinicCityAnchorTag} {this.nearbyBrandClinicDistance} miles away that
            you may want to consider if the wait gets too long.
          </p>
        )}
        <NoBottomMarginParagraph>
          If you are looking to learn more about urgent care including statistics, trends,{' '}
          {getFaqsAnchorTag()} and other useful information, check out our {getBlogAnchorTag()} to
          empower your knowledge as a healthcare consumer. {this.cityGuideContent}
        </NoBottomMarginParagraph>
      </span>
    );
  }
}

export default NonPartnerUrgentCareV3;
